import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import Spinner from 'components/Spinner'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import moment from 'moment'
import { useStyletron } from 'styletron-react'
import { getFormattedDate } from 'utilities/date'
import { percentageFormatter } from 'utilities/formatters'
import type { Column, TableRow } from './buildTable'

const t = buildTranslate('components.ai.purchase_data')

const monthLabel = ({ month, soFar }: Column) => {
  const label = getFormattedDate(moment(month), 'monthly')
  return soFar ? t('table.month_so_far', { month: label }) : label
}

const Share = ({ share }: { share: number | null }) => {
  if (share !== null) return <>{percentageFormatter(share, 0)}</>
  return (
    <>
      <span aria-hidden="true">{t('table.no_purchases')}</span>
      <span className="sr-only">{t('table.no_purchases_label')}</span>
    </>
  )
}

type Props = {
  columns: Column[]
  rows: TableRow[]
  locationNames: Map<number, string>
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

const PurchaseDataTable = ({
  columns,
  rows,
  locationNames,
  isLoading,
  isError,
  onRetry,
}: Props) => {
  const [css] = useStyletron()

  if (isLoading) {
    return <Spinner isLoading className="text-center" size="2x" />
  }

  if (isError) {
    return (
      <div className="text-center py-l">
        <Text.BodyText3 tag="p" color="grey70" className="mb-s">
          {t('error')}
        </Text.BodyText3>
        <Button kind="secondary" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <Text.BodyText3 tag="p" color="grey70" className="text-center">
        {t('empty')}
      </Text.BodyText3>
    )
  }

  const rowName = (locationId: number | null) =>
    locationId === null
      ? t('table.all_locations')
      : locationNames.get(locationId) ?? ''
  const partialColumn = columns.find(column => column.soFar)
  const cellClass = `px-m py-s align-top text-left ${css({
    borderBottom: `1px solid ${palette.grey30}`,
  })}`

  return (
    <div className="table-responsive">
      <table className="w-100">
        <caption className="sr-only">{t('title')}</caption>
        <thead>
          <tr>
            <th scope="col" className="px-m pb-l align-top text-left">
              <Text.BodyText4 color="grey90" bold>
                {t('table.location')}
              </Text.BodyText4>
            </th>
            {columns.map(column => (
              <th
                key={column.month}
                scope="col"
                className="px-m pb-l align-top text-left"
              >
                <Text.BodyText4 color="grey90" bold>
                  {monthLabel(column)}
                </Text.BodyText4>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.locationId ?? 'all'}>
              <th scope="row" className={cellClass}>
                <Text.BodyText4 color="grey70">
                  {rowName(row.locationId)}
                </Text.BodyText4>
              </th>
              {row.shares.map((share, index) => (
                <td key={columns[index].month} className={cellClass}>
                  <Text.BodyText4 color="grey70">
                    <Share share={share} />
                  </Text.BodyText4>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {partialColumn && (
        <Text.BodyText4 tag="p" color="grey70" className="mt-s">
          {t('table.partial_month_note', {
            month: moment(partialColumn.month).format('MMMM'),
          })}
        </Text.BodyText4>
      )}
    </div>
  )
}

export default PurchaseDataTable
